import { DefaultTool } from '@blocksuite/affine-block-surface';
import type { MermaidElementModel } from '@blocksuite/affine-model';
import { Bound } from '@blocksuite/global/gfx';
import type { PointerEventState } from '@blocksuite/std';
import { BaseTool, type GfxController } from '@blocksuite/std/gfx';

import {
  addMermaidElement,
  getCollisionAwareMermaidBound,
  trackMermaidCreated,
} from './create.js';
import {
  DEFAULT_MERMAID_TEMPLATE,
  type MermaidTemplateId,
} from './templates.js';

export type MermaidToolOption = {
  template: MermaidTemplateId;
};

export class MermaidTool extends BaseTool<MermaidToolOption> {
  static override toolName = 'mermaid';

  private _draggingElement: MermaidElementModel | null = null;
  private _draggingElementId: string | null = null;

  private get _blocked() {
    return this.std.store.readonly || this.gfx.viewport.locked;
  }

  constructor(gfx: GfxController) {
    super(gfx);
    this.activatedOption = {
      template: DEFAULT_MERMAID_TEMPLATE,
    };
  }

  private _selectAndExit(id: string) {
    const element = this.gfx.getElementById(id);
    if (!element) return;

    this.controller.setTool(DefaultTool);
    this.gfx.selection.set({
      elements: [id],
      editing: false,
    });
  }

  override click(e: PointerEventState) {
    if (this._blocked) return;

    this.doc.captureSync();

    const [x, y] = this.gfx.viewport.toModelCoord(e.point.x, e.point.y);
    const bound = getCollisionAwareMermaidBound(this.gfx, [x, y]);
    const id = addMermaidElement(
      this.gfx,
      bound,
      this.activatedOption.template
    );

    trackMermaidCreated(
      this.gfx,
      this.activatedOption.template,
      'canvas:draw',
      'template'
    );
    this._selectAndExit(id);
  }

  override deactivate() {
    if (!this._draggingElementId) return;
    this.gfx.deleteElement(this._draggingElementId);
    this._draggingElement = null;
    this._draggingElementId = null;
  }

  override dragStart(e: PointerEventState) {
    if (this._blocked) return;

    this.doc.captureSync();
    const [x, y] = this.gfx.viewport.toModelCoord(e.point.x, e.point.y);
    const id = addMermaidElement(
      this.gfx,
      new Bound(x, y, 1, 1),
      this.activatedOption.template
    );

    this._draggingElementId = id;
    this._draggingElement = this.gfx.getElementById(id) as MermaidElementModel;
    this._draggingElement.stash('xywh');
  }

  override dragMove() {
    if (this._blocked || !this._draggingElementId || !this._draggingElement) {
      return;
    }

    const { startX, startY, endX, endY } = this.controller.draggingArea$.peek();
    const bound = new Bound(
      Math.min(startX, endX),
      Math.min(startY, endY),
      Math.abs(startX - endX),
      Math.abs(startY - endY)
    );

    this.gfx.updateElement(this._draggingElement, {
      xywh: bound.serialize(),
    });
  }

  override dragEnd() {
    const id = this._draggingElementId;
    const model = this._draggingElement;
    if (!id || !model) return;

    model.pop('xywh');

    const draggingArea = this.controller.draggingArea$.peek();
    if (draggingArea.w < 20 && draggingArea.h < 20) {
      this.gfx.deleteElement(id);
      this._draggingElement = null;
      this._draggingElementId = null;
      this.controller.setTool(DefaultTool);
      return;
    }

    this._draggingElement = null;
    this._draggingElementId = null;
    this.doc.captureSync();

    trackMermaidCreated(
      this.gfx,
      this.activatedOption.template,
      'canvas:draw',
      'tool'
    );
    this._selectAndExit(id);
  }
}
